import {useEffect, useRef} from "react";
import {getOrCreateSourceNode, resumeAudioContext} from "./audioContext";

const BAND_COUNT = 48;

function emptyReading() {
  return {level: 0, bands: new Uint8Array(BAND_COUNT)};
}

/**
 * Analyses a MediaStream (microphone) or HTMLMediaElement (TTS/remote audio) in real time and
 * exposes the result through a ref — `{ level: 0..1, bands: Uint8Array(48) }` — updated every
 * animation frame. A ref (not state) on purpose: VoiceOrb reads it inside its own rAF render
 * loop, so audio data never triggers a React re-render.
 *
 * `source` may be a MediaStream, an HTMLMediaElement, or null/undefined (nothing to analyse —
 * the ref just stays at rest).
 */
export function useAudioLevel(source) {
  const readingRef = useRef(emptyReading());

  useEffect(() => {
    if (!source) {
      readingRef.current = emptyReading();
      return undefined;
    }

    const audioCtx = resumeAudioContext();
    const entry = audioCtx ? getOrCreateSourceNode(audioCtx, source) : null;
    if (!audioCtx || !entry) {
      readingRef.current = emptyReading();
      return undefined;
    }

    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.75;

    entry.node.connect(analyser);
    // A MediaElementAudioSourceNode intercepts the element's normal output — route it back to
    // the speakers through the analyser (a plain pass-through node) or the TTS/remote audio
    // would go silent. A MediaStreamAudioSourceNode (the microphone) must NOT be connected to
    // the destination, or the user would hear their own mic echoed back in real time.
    if (entry.isElement) {
      analyser.connect(audioCtx.destination);
    }

    const data = new Uint8Array(analyser.frequencyBinCount);
    const bands = new Uint8Array(BAND_COUNT);
    const step = Math.max(1, Math.floor(data.length / BAND_COUNT));

    let raf;
    const tick = () => {
      analyser.getByteFrequencyData(data);

      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      const level = Math.min(1, sum / data.length / 85);

      for (let b = 0; b < BAND_COUNT; b++) bands[b] = data[b * step] || 0;

      readingRef.current = {level, bands};
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      try {
        entry.node.disconnect(analyser);
      } catch {
        // already disconnected
      }
      try {
        analyser.disconnect();
      } catch {
        // already disconnected
      }
      readingRef.current = emptyReading();
    };
  }, [source]);

  return readingRef;
}
